import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  PartyPopper, User, CalendarDays, GraduationCap,
  BookOpen, CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, Check, Star, Video, MessageSquare, ClipboardList, Rocket, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { StudentSchedule } from './StudentSchedule';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Welcome',              icon: PartyPopper,   desc: 'You are all set!' },
  { id: 2, title: 'Your Profile',         icon: User,          desc: 'Tell us about yourself' },
  { id: 3, title: 'Preferred Times',      icon: CalendarDays,  desc: 'When do you like to study?' },
  { id: 4, title: 'Meet Your Teacher',    icon: GraduationCap, desc: 'Your instructor' },
  { id: 5, title: 'Book First Class',     icon: Calendar,      desc: 'Schedule your inaugural lesson' },
  { id: 6, title: 'How It Works',         icon: BookOpen,      desc: 'Platform quick guide' },
  { id: 7, title: 'Ready!',               icon: CheckCircle2,  desc: 'Start your journey' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const GOALS = [
  'Business English',
  'Academic English / IELTS / TOEFL',
  'Conversational English',
  'Travel & Everyday Communication',
  'Job Interviews',
  'Writing & Grammar',
];

const inputCls = 'w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all';

// ── Step 1: Welcome ──────────────────────────────────────────────────────────
const Step1 = ({ userName }) => (
  <div className="text-center space-y-6 py-4">
    <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center mx-auto">
      <PartyPopper className="w-10 h-10 text-[#F5A623]" />
    </div>
    <div>
      <h3 className="text-2xl font-bold text-[#1A2E6E] font-['Sora'] mb-2">
        Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}!
      </h3>
      <p className="text-[#1A2E6E]/60 leading-relaxed">
        Your account has been approved. Let's take a quick 2-minute tour to get you ready for your first lesson.
      </p>
    </div>
    <div className="grid grid-cols-3 gap-3 text-center">
      {[
        { icon: Video,         label: 'Live Classes' },
        { icon: ClipboardList, label: 'Activities' },
        { icon: MessageSquare, label: 'Direct Chat' },
      ].map(({ icon: Icon, label }) => (
        <div key={label} className="p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
          <Icon className="w-6 h-6 text-[#1A2E6E] mx-auto mb-2" />
          <p className="text-xs font-medium text-[#1A2E6E]/70">{label}</p>
        </div>
      ))}
    </div>
  </div>
);

// ── Step 2: Profile ──────────────────────────────────────────────────────────
const Step2 = ({ data, onChange, levelFromTest }) => (
  <div className="space-y-5">
    <div>
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Your Learning Goals</label>
      <p className="text-xs text-[#1A2E6E]/50 mb-3">Select all that apply</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {GOALS.map(goal => {
          const selected = (data.goals || []).includes(goal);
          return (
            <button
              key={goal}
              type="button"
              onClick={() => {
                const goals = data.goals || [];
                onChange({ ...data, goals: selected ? goals.filter(g => g !== goal) : [...goals, goal] });
              }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-sm transition-all ${
                selected
                  ? 'border-[#1A2E6E] bg-[#1A2E6E]/5 text-[#1A2E6E] font-medium'
                  : 'border-[#E8EAF0] text-[#1A2E6E]/60 hover:border-[#1A2E6E]/30'
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-[#1A2E6E] bg-[#1A2E6E]' : 'border-[#D1D5DB]'}`}>
                {selected && <Check className="w-3 h-3 text-white" />}
              </div>
              {goal}
            </button>
          );
        })}
      </div>
    </div>
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <label htmlFor="student-onboarding-level" className="block text-sm font-medium text-[#1A2E6E]">Current English Level</label>
        {levelFromTest && data.level === levelFromTest && (
          <span className="text-[10px] bg-[#4CAF50]/10 text-[#4CAF50] px-1.5 py-0.5 rounded-full font-semibold">From level test</span>
        )}
      </div>
      <select
        id="student-onboarding-level"
        value={data.level || ''}
        onChange={e => onChange({ ...data, level: e.target.value })}
        className={inputCls + ' bg-white'}
      >
        <option value="">Select your level</option>
        {['A1 – Beginner', 'A2 – Elementary', 'B1 – Intermediate', 'B2 – Upper Intermediate', 'C1 – Advanced', 'C2 – Proficient'].map(l => (
          <option key={l} value={l}>{l}</option>
        ))}
      </select>
    </div>
    <div>
      <label htmlFor="student-onboarding-referral" className="block text-sm font-medium text-[#1A2E6E] mb-1.5">How did you hear about us?</label>
      <select
        id="student-onboarding-referral"
        value={data.referral || ''}
        onChange={e => onChange({ ...data, referral: e.target.value })}
        className={inputCls + ' bg-white'}
      >
        <option value="">Select an option</option>
        {['Google Search', 'Instagram', 'Facebook', 'Friend / Referral', 'LinkedIn', 'WhatsApp Group', 'Other'].map(r => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
    </div>
  </div>
);

// ── Step 3: Preferred Schedule ───────────────────────────────────────────────
const Step3 = ({ data, onChange }) => {
  const preferred = data.preferred_days || [];
  const toggleDay = (day) => {
    onChange({
      ...data,
      preferred_days: preferred.includes(day) ? preferred.filter(d => d !== day) : [...preferred, day],
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Preferred Days</label>
        <p className="text-xs text-[#1A2E6E]/50 mb-3">Your teacher will try to schedule classes on these days</p>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {DAYS.map(day => {
            const sel = preferred.includes(day);
            return (
              <button key={day} type="button" onClick={() => toggleDay(day)}
                className={`py-3 rounded-xl border text-xs font-medium transition-all ${
                  sel ? 'border-[#1A2E6E] bg-[#1A2E6E] text-white' : 'border-[#E8EAF0] text-[#1A2E6E]/60 hover:border-[#1A2E6E]/30'
                }`}>
                {DAY_LABELS[day]}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Preferred Time of Day</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {['Morning (8h–12h)', 'Afternoon (12h–17h)', 'Evening (17h–21h)', 'Night (21h–23h)'].map(t => {
            const sel = (data.preferred_times || []).includes(t);
            return (
              <button key={t} type="button"
                onClick={() => {
                  const times = data.preferred_times || [];
                  onChange({ ...data, preferred_times: sel ? times.filter(x => x !== t) : [...times, t] });
                }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm text-left transition-all ${
                  sel ? 'border-[#1A2E6E] bg-[#1A2E6E]/5 text-[#1A2E6E] font-medium' : 'border-[#E8EAF0] text-[#1A2E6E]/60 hover:border-[#1A2E6E]/30'
                }`}>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${sel ? 'border-[#1A2E6E] bg-[#1A2E6E]' : 'border-[#D1D5DB]'}`}>
                  {sel && <Check className="w-3 h-3 text-white" />}
                </div>
                {t}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Extra notes for your teacher <span className="text-[#1A2E6E]/40">(optional)</span></label>
        <textarea
          value={data.schedule_notes || ''}
          onChange={e => onChange({ ...data, schedule_notes: e.target.value })}
          placeholder="e.g. I'm free every evening after 7pm on weekdays"
          rows={2}
          className={inputCls + ' resize-none'}
        />
      </div>
    </div>
  );
};

// ── Step 4: Meet Your Teacher ─────────────────────────────────────────────────
const Step4 = ({ teachers, loading }) => {
  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <Loader2 className="w-6 h-6 text-[#D72B2B] animate-spin" />
    </div>
  );
  if (!teachers.length) return (
    <div className="text-center py-8">
      <GraduationCap className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-3" />
      <p className="text-[#1A2E6E]/60 text-sm">Your teacher will be assigned soon.<br/>You'll be notified when they reach out.</p>
    </div>
  );
  return (
    <div className="space-y-4">
      <p className="text-sm text-[#1A2E6E]/60">Meet the team who will guide your English journey:</p>
      {teachers.map(t => (
        <div key={t.id} className="flex items-center gap-4 p-5 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
          <div className="w-14 h-14 rounded-full bg-[#1A2E6E] flex items-center justify-center text-white font-bold text-xl shrink-0">
            {t.name?.charAt(0)?.toUpperCase() || 'T'}
          </div>
          <div>
            <p className="font-semibold text-[#1A2E6E]">{t.name}</p>
            <p className="text-sm text-[#1A2E6E]/50 flex items-center gap-1 mt-0.5">
              <Star className="w-3 h-3 text-[#F5A623]" />
              Certified English Teacher
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Step 5: How It Works ──────────────────────────────────────────────────────
const HOW_ITEMS = [
  {
    icon: Video,
    color: 'bg-[#4CAF50]/10 text-[#4CAF50]',
    title: 'Live Online Classes',
    desc: 'Join your class via the meeting link in "My Classes". Classes run on Google Meet or Zoom.',
  },
  {
    icon: ClipboardList,
    color: 'bg-[#1A2E6E]/10 text-[#1A2E6E]',
    title: 'Activities & Exercises',
    desc: 'Your teacher assigns activities in the "Activities" section. Submit text or files before the due date.',
  },
  {
    icon: Star,
    color: 'bg-[#F5A623]/10 text-[#F5A623]',
    title: 'Grades & Feedback',
    desc: 'Once your teacher reviews your work, you\'ll see your grade and personalised feedback.',
  },
  {
    icon: MessageSquare,
    color: 'bg-[#D72B2B]/10 text-[#D72B2B]',
    title: 'Direct Chat',
    desc: 'Have a question? Use the Chat to reach your teacher directly, even before your first class.',
  },
];

// ── Step 6: How It Works ────────────────────────────────────────────────────
const Step6 = () => (
  <div className="space-y-4">
    {HOW_ITEMS.map(({ icon: Icon, color, title, desc }) => (
      <div key={title} className="flex gap-4 p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="font-semibold text-[#1A2E6E] text-sm mb-0.5">{title}</p>
          <p className="text-xs text-[#1A2E6E]/60 leading-relaxed">{desc}</p>
        </div>
      </div>
    ))}
  </div>
);

// ── Step 7: Ready ─────────────────────────────────────────────────────────────
const Step7 = ({ userName }) => (
  <div className="text-center space-y-6 py-4">
    <div className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto">
      <Rocket className="w-10 h-10 text-[#4CAF50]" />
    </div>
    <div>
      <h3 className="text-2xl font-bold text-[#1A2E6E] font-['Sora'] mb-2">
        You're all set{userName ? `, ${userName.split(' ')[0]}` : ''}!
      </h3>
      <p className="text-[#1A2E6E]/60 leading-relaxed">
        Your teacher will contact you shortly to schedule your first class. Meanwhile, explore your dashboard!
      </p>
    </div>
    <div className="flex flex-col gap-2 text-left max-w-xs mx-auto">
      {[
        'Check "My Classes" for upcoming lessons',
        'Look at "Activities" for assignments',
        'Say hi in Chat!',
      ].map(item => (
        <div key={item} className="flex items-center gap-3">
          <Check className="w-4 h-4 text-[#4CAF50] shrink-0" />
          <p className="text-sm text-[#1A2E6E]/70">{item}</p>
        </div>
      ))}
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export const StudentOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [levelFromTest, setLevelFromTest] = useState('');
  const [stepData, setStepData] = useState({
    2: { goals: [], level: '', referral: '' },
    3: { preferred_days: [], preferred_times: [], schedule_notes: '' },
  });

  // Maps level-test result codes → onboarding dropdown values
  const LEVEL_MAP = {
    'A1-A2': 'A1 – Beginner',
    'B1':    'B1 – Intermediate',
    'B2':    'B2 – Upper Intermediate',
    'C1-C2': 'C1 – Advanced',
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [onboardingRes, levelRes] = await Promise.all([
          axios.get(`${API_URL}/api/student/onboarding`),
          axios.get(`${API_URL}/api/student/level-test-result`).catch(() => ({ data: { has_result: false } })),
        ]);

        if (onboardingRes.data.completed) {
          navigate('/student/dashboard', { replace: true });
          return;
        }

        const saved = onboardingRes.data.step_data || {};
        const testLevel = levelRes.data.has_result
          ? (LEVEL_MAP[levelRes.data.level] || '')
          : '';
        if (testLevel) setLevelFromTest(testLevel);

        setStepData(prev => ({
          2: {
            ...prev[2],
            ...(saved[2] || {}),
            // Pre-fill level from test result only if the student hasn't already set one
            level: (saved[2]?.level) || testLevel || prev[2].level,
          },
          3: { ...prev[3], ...(saved[3] || {}) },
        }));
        setCompletedSteps(onboardingRes.data.completed_steps || []);
        setCurrentStep(onboardingRes.data.current_step || 1);
      } catch {
        // continue without saved progress
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch teachers when reaching Step 4
  useEffect(() => {
    if (currentStep === 4 && !teachers.length) {
      setTeachersLoading(true);
      axios
        .get(`${API_URL}/api/student/teachers`)
        .then(res => setTeachers(res.data))
        .catch(() => {})
        .finally(() => setTeachersLoading(false));
    }
  }, [currentStep, teachers.length]);

  const saveStep = async (step, data) => {
    try {
      await axios.put(`${API_URL}/api/student/onboarding/step`, { step, data });
      setCompletedSteps(prev => prev.includes(step) ? prev : [...prev, step]);
    } catch {
      // non-blocking
    }
  };

  const handleNext = async () => {
    const data = stepData[currentStep] || {};
    if ([2, 3].includes(currentStep)) {
      await saveStep(currentStep, data);
    } else {
      await saveStep(currentStep, {});
    }
    setCurrentStep(s => Math.min(s + 1, STEPS.length));
  };

  // Step 5: skip scheduling without booking
  const handleSkipSchedule = async () => {
    await saveStep(5, { skipped: true });
    setCurrentStep(6);
  };

  // Step 5: booking confirmed → advance
  const handleBooked = async (bookedClass) => {
    await saveStep(5, { booked: true, class_id: bookedClass.class_id });
    setCurrentStep(6);
  };

  const handleBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const handleFinish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/student/onboarding/complete`);
      toast.success("You're all set! Welcome to the platform.");
      navigate('/student/dashboard', { replace: true });
    } catch {
      toast.error('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC]">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  const stepConfig = STEPS[currentStep - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FC] to-[#EEF0F8] flex flex-col">
      {/* Top bar */}
      <div className="w-full bg-white border-b border-[#E8EAF0] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#4CAF50] rounded-lg flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-[#1A2E6E] font-['Sora'] hidden sm:block">Getting Started</span>
        </div>
        <span className="text-sm text-[#1A2E6E]/50">Step {currentStep} of {STEPS.length}</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-start px-4 py-8 sm:py-12">
        <div className="w-full max-w-lg">

          {/* Stepper */}
          <div className="flex items-center mb-10">
            {STEPS.map((step, idx) => {
              const done = completedSteps.includes(step.id);
              const active = currentStep === step.id;
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                      done ? 'bg-[#4CAF50] border-[#4CAF50]' :
                      active ? 'bg-[#1A2E6E] border-[#1A2E6E]' :
                               'bg-white border-[#E8EAF0]'
                    }`}>
                      {done ? <Check className="w-3.5 h-3.5 text-white" /> :
                              <Icon className={`w-3.5 h-3.5 ${active ? 'text-white' : 'text-[#1A2E6E]/30'}`} />}
                    </div>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1.5 ${done ? 'bg-[#4CAF50]' : 'bg-[#E8EAF0]'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl border border-[#E8EAF0] shadow-sm overflow-hidden"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-[#E8EAF0] bg-gradient-to-r from-[#4CAF50]/5 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#4CAF50]/10 rounded-xl flex items-center justify-center">
                    <stepConfig.icon className="w-5 h-5 text-[#4CAF50]" />
                  </div>
                  <div>
                    <h2 className="font-bold text-[#1A2E6E] text-lg font-['Sora']">{stepConfig.title}</h2>
                    <p className="text-sm text-[#1A2E6E]/60">{stepConfig.desc}</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                {currentStep === 1 && <Step1 userName={user?.name} />}
                {currentStep === 2 && (
                  <Step2
                    data={stepData[2]}
                    onChange={d => setStepData(prev => ({ ...prev, 2: d }))}
                    levelFromTest={levelFromTest}
                  />
                )}
                {currentStep === 3 && (
                  <Step3
                    data={stepData[3]}
                    onChange={d => setStepData(prev => ({ ...prev, 3: d }))}
                  />
                )}
                {currentStep === 4 && <Step4 teachers={teachers} loading={teachersLoading} />}
                {currentStep === 5 && (
                  <StudentSchedule
                    mode="embed"
                    onBooked={handleBooked}
                    onSkip={handleSkipSchedule}
                  />
                )}
                {currentStep === 6 && <Step6 />}
                {currentStep === 7 && <Step7 userName={user?.name} />}
              </div>

              {/* Footer nav — hidden on step 5 (scheduler has its own CTAs) */}
              {currentStep !== 5 && (
                <div className="px-6 py-4 border-t border-[#E8EAF0] flex items-center justify-between bg-[#F8F9FC]">
                  <button onClick={handleBack} disabled={currentStep === 1}
                    className="flex items-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-white transition-colors text-sm font-medium disabled:opacity-30">
                    <ArrowLeft size={16} /> Back
                  </button>

                  {currentStep < STEPS.length ? (
                    <button onClick={handleNext}
                      className="flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium">
                      Continue <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button onClick={handleFinish} disabled={saving}
                      className="flex items-center gap-2 px-6 py-2.5 bg-[#4CAF50] text-white rounded-xl hover:bg-[#388E3C] transition-colors font-medium disabled:opacity-50">
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                      Go to Dashboard
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <p className="text-center text-xs text-[#1A2E6E]/30 mt-6">
            Your progress is saved automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentOnboarding;
